import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ForecastModelState,
  ForecastModelStatusResponse,
  getForecastModelStatus,
} from "../insightsAdminApi";
import {
  formatTrainingWindow,
  hasModelMovedPastQueue,
} from "../trainingWindow";

/**
 * What the merchant's forecast section would say, with the numbers behind it.
 *
 * The server resolves `state` through the same helper the Merchant Hub uses, so
 * this panel cannot report something the merchant does not see — except while
 * a rebuild we just queued is still running, when Ready would be the previous
 * model.
 */
interface Props {
  businessId?: string;
  /** Bumped by the rebuild button so the panel re-reads after a resync POST. */
  refreshToken?: number;
  /** ISO time of the rebuild click; kept until lastTrainedAt moves past it. */
  rebuildQueuedAt?: string;
  onRebuildSettled?: () => void;
}

const STATE_TAG: Record<
  ForecastModelState,
  { color: string; label: string; meaning: string }
> = {
  ready: {
    color: "green",
    label: "Ready",
    meaning: "The merchant sees real predictions.",
  },
  belowQualityBar: {
    color: "orange",
    label: "Below quality bar",
    meaning: "A model was fitted, but it scored under the floor to be served.",
  },
  modelUnusable: {
    color: "red",
    label: "Model will not load",
    meaning: "A model is stored but the reader cannot parse it. This is a bug.",
  },
  noModelYet: {
    color: "gold",
    label: "No model yet",
    meaning: "Enough history, but no model has been fitted for this merchant.",
  },
  learning: {
    color: "default",
    label: "Learning",
    meaning: "Not enough trading history yet.",
  },
};

const POLL_MS = 5000;
const POLL_GIVE_UP_MS = 15 * 60 * 1000;

/** Dev-like stages enforce no floor, and "needs -1" reads as a broken gate. */
function renderFloor(floor: number | null): string {
  return floor === null ? "no minimum on this stage" : `needs ${floor}`;
}

export default function ForecastModelStatus({
  businessId,
  refreshToken,
  rebuildQueuedAt,
  onRebuildSettled,
}: Props) {
  const [status, setStatus] = useState<ForecastModelStatusResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [gaveUpPolling, setGaveUpPolling] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!businessId) return;

    setIsLoading(true);
    setError(undefined);

    try {
      setStatus(await getForecastModelStatus(businessId));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not read the model status"
      );
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const isRebuilding = Boolean(
    (rebuildQueuedAt &&
      !hasModelMovedPastQueue(
        status?.lastTrainedAt ?? null,
        rebuildQueuedAt
      )) ||
    status?.resyncRequestedAt
  );

  useEffect(() => {
    if (
      rebuildQueuedAt &&
      hasModelMovedPastQueue(status?.lastTrainedAt ?? null, rebuildQueuedAt)
    ) {
      onRebuildSettled?.();
    }
  }, [rebuildQueuedAt, status?.lastTrainedAt, onRebuildSettled]);

  useEffect(() => {
    if (!businessId || !isRebuilding || gaveUpPolling) return;

    const interval = setInterval(() => void load(), POLL_MS);
    const giveUp = setTimeout(() => setGaveUpPolling(true), POLL_GIVE_UP_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(giveUp);
    };
  }, [businessId, isRebuilding, gaveUpPolling, load]);

  useEffect(() => {
    setGaveUpPolling(false);
  }, [rebuildQueuedAt]);

  if (error) {
    return <Alert type="error" showIcon message={error} />;
  }

  if (!status) {
    if (rebuildQueuedAt) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Rebuild in progress"
          description="Reading model status…"
        />
      );
    }

    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {isLoading ? "Reading model status…" : "No model status."}
      </Typography.Text>
    );
  }

  const tag = STATE_TAG[status.state];
  const scoreShort =
    status.minValidationScore !== null &&
    status.validationScore < status.minValidationScore;
  const dataRange = formatTrainingWindow(
    status.dataThrough,
    status.trainingDays,
    status.dataFrom
  );

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Space>
        {isRebuilding && <Spin size="small" />}
        <Tag color={isRebuilding ? "processing" : tag.color}>
          {isRebuilding ? "Retraining" : tag.label}
        </Tag>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {isRebuilding
            ? "Figures and the forecast are being rebuilt. The numbers below are still the previous model."
            : tag.meaning}
        </Typography.Text>
        <Button size="small" onClick={load} loading={isLoading}>
          Refresh
        </Button>
      </Space>

      {isRebuilding ? (
        <Alert
          type="warning"
          showIcon
          message="Rebuild in progress"
          description={
            gaveUpPolling
              ? "This is taking longer than usual. Click Refresh, or come back in a few minutes."
              : "This usually takes a few minutes. The panel refreshes on its own."
          }
        />
      ) : (
        <Alert
          type={status.state === "modelUnusable" ? "error" : "info"}
          showIcon
          message={status.nextAction}
        />
      )}

      {status.hasModelPointer && !status.isModelLoadable && (
        <Alert
          type="error"
          showIcon
          message="A model is stored but could not be read"
          description={
            status.modelUri
              ? `The reader rejected ${status.modelUri}. The merchant is being served weekday averages.`
              : "The reader rejected the stored artifact."
          }
        />
      )}

      {!status.hasSnapshot && (
        <Alert
          type="info"
          showIcon
          message="This merchant has never been built — run a rebuild first."
        />
      )}

      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Validation score">
          <Typography.Text type={scoreShort ? "warning" : "success"}>
            {status.validationScore.toFixed(4)}
          </Typography.Text>
          <Typography.Text type="secondary">
            {" "}
            ({renderFloor(status.minValidationScore)})
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="History">
          {status.trainingDays} days
          <Typography.Text type="secondary">
            {" "}
            ({renderFloor(status.minTrainingDays)})
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Last trained">
          {status.lastTrainedAt
            ? new Date(status.lastTrainedAt).toLocaleString()
            : "never"}
        </Descriptions.Item>
        <Descriptions.Item label="Data through">{dataRange}</Descriptions.Item>
        <Descriptions.Item label="Features">
          {status.featureCount} columns
        </Descriptions.Item>
        <Descriptions.Item label="Event tags">
          {status.tagFeatures.length ? (
            status.tagFeatures.join(", ")
          ) : (
            <Typography.Text type="warning">
              none reached the model
            </Typography.Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Space>
  );
}
